import React, { useEffect, useMemo, useState, Dispatch, SetStateAction } from "react";
import {
  Button,
  Select,
  MenuItem,
  FormControl,
  Box,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  DialogActions,
  Checkbox,
  ListItemText,
} from "@mui/material";
import {
  ApprovalStatus,
  DateOptions,
  StatusOptions,
} from "../../types/FilterEnums";
import ReactDatePicker from "react-datepicker";
import { useWindowSize } from "../../utils/hooks/useWindowSize";
import { Close } from "@mui/icons-material";
import CustomInput from "./CustomInput";
import { dStyle, mStyle, style } from "./style";
import { useTranslation } from "react-i18next";
import { ReactComponent as IconFilterFilled } from "../../assets/images/icons/filterFilled.svg";
import { TripRequestBody } from "../../types/api";
import { useBusinessProfile } from "../../utils/hooks/useBusinessProfile";

interface FilterBarProps {
  filters: TripRequestBody | null;
  setFilters: Dispatch<SetStateAction<TripRequestBody | null>>;
  onFilterApply: (filters: TripRequestBody) => void;
}
const FilterBar: React.FC<FilterBarProps> = ({ filters, setFilters, onFilterApply }) => {
  const windowsWidth = useWindowSize();
  const isMobile = windowsWidth < 768;
  const { businessProfile, legacyData } = useBusinessProfile();


  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [startDate, setStartDate] = useState<Date | undefined>(() => {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 15);
    return startDate;
  });
  const [endDate, setEndDate] = useState<Date | undefined>(() => {
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + 15);
    return endDate;
  });
  const [filtersRestored, setFiltersRestored] = useState<boolean>(false);
  
  // Sincronizza startDate e endDate con i valori dei filtri esterni
  useEffect(() => {
    if (filters?.dateFrom) {
      setStartDate(new Date(filters.dateFrom));
    } else {
      // Se non ci sono filtri esterni, inizializza con le date di default
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 15);
      setStartDate(startDate);
    }
    if (filters?.dateTo) {
      setEndDate(new Date(filters.dateTo));
    } else {
      // Se non ci sono filtri esterni, inizializza con le date di default
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + 15);
      setEndDate(endDate);
    }
  }, [filters?.dateFrom, filters?.dateTo]);
  

  const formatDateWithTimezone = (dateString: string | Date): string => {
    const date = new Date(dateString);
    
    // Format the date in Italian locale to get the correct timezone
    const options: Intl.DateTimeFormatOptions = {
      timeZone: 'Europe/Rome',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZoneName: 'shortOffset'
    };

    const formatter = new Intl.DateTimeFormat('it-IT', options);
    const formatted = formatter.format(date);
    
    // Extract the timezone offset
    const timezoneMatch = formatted.match(/([+-]\d{2}:\d{2})$/);
    const offset = timezoneMatch ? timezoneMatch[1] : '+02:00';
    
    // Format the date in ISO format
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offset}`;
  };
  // Rimuovo la gestione di savedFilters e l'inizializzazione locale dei filtri

  const handleToggleDropdown = () => {
    setIsDropdownOpen((prev) => !prev);
  };

  const handleFilterChange = (
    field: keyof TripRequestBody,
    value:
      | string
      | string[]
      | number
      | number[]
      | [Date | undefined, Date | undefined]
      | Date
      | undefined
  ) => {
    if (!filters) return;
    setFilters({
      ...filters,
      [field]: value,
    });
  };

  const handleApplyFilters = () => {
    if (!filters) return;
    
    // Crea copie delle date per evitare di modificare gli oggetti originali
    const startDateCopy = startDate ? new Date(startDate) : undefined;
    const endDateCopy = endDate ? new Date(endDate) : undefined;
    
    if (startDateCopy) startDateCopy.setHours(0, 0, 0, 0); // 00:00
    if (endDateCopy) endDateCopy.setHours(23, 59, 59, 999); // 23:59

    const updatedFilters = {
      ...filters,
      dateFrom: startDateCopy ? formatToLocalISOString(startDateCopy) : undefined,
      dateTo: endDateCopy ? formatToLocalISOString(endDateCopy) : undefined,
    };
    setFilters(updatedFilters);
    setIsDropdownOpen(false);
    setFiltersRestored(false); // Reset flag when applying new filters
    onFilterApply(updatedFilters);
  };

  const formatToLocalISOString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Mesi da 0 a 11
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    const milliseconds = String(date.getMilliseconds()).padStart(3, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
  };

  // Rimuovo la gestione di savedFilters e l'inizializzazione locale dei filtri

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;

    if (start && end) {
      const adjustedStart = new Date(start);
      const adjustedEnd = new Date(end);

      // Imposta gli orari corretti
      adjustedStart.setHours(0, 0, 0, 0); // 00:00
      adjustedEnd.setHours(23, 59, 59, 999); // 23:59

      const diffInDays = Math.floor(
        (adjustedEnd.getTime() - adjustedStart.getTime()) / (1000 * 3600 * 24)
      );

      if (diffInDays > 30) {
        // Limita il range a massimo 30 giorni
        const newEnd = new Date(adjustedStart);
        newEnd.setDate(adjustedStart.getDate() + 30); // Aggiungi 30 giorni
        newEnd.setHours(23, 59, 59, 999); // Orario 23:59

        setStartDate(adjustedStart);
        setEndDate(newEnd);
      } else {
        setStartDate(adjustedStart);
        setEndDate(adjustedEnd);
      }
    } else {
      setStartDate(start || undefined);
      setEndDate(end || undefined);
    }
  };

  const { t } = useTranslation();

  const formatSelectLabel = (values: string[]) => {
    if (values.length === 1) {
      return translateMap[values[0]].toUpperCase(); // Mostra il singolo valore selezionato
    }
    if (values.length > 1) {
      return translateMap[values[0]].toUpperCase() + "..."; // Mostra il primo valore seguito da "..."
    }
    return ""; // Nessun valore selezionato
  };

  const areFiltersValid = useMemo(() => {
    // Controlla se tutti i campi obbligatori sono compilati
    const isMilestoneValueValid =
      filters?.milestoneValue && filters.milestoneValue.length > 0;
    const isApprovalValid = filters?.approval && filters.approval.length > 0;
    const isDateTypeValid = !!filters?.dateType;
    const isDateRangeValid = startDate && endDate && startDate <= endDate;

    return (
      isMilestoneValueValid &&
      isApprovalValid &&
      isDateTypeValid &&
      isDateRangeValid
    );
  }, [filters, startDate, endDate]);

  const translateMap: Record<string, string> = {
    ASSIGNED: t("screens.trips.statusOptions.ASSIGNED"),
    ALLOCATED: t("screens.trips.statusOptions.ALLOCATED"),
    "IN PROGRESS": t("screens.trips.statusOptions.IN_PROGRESS"),
    "WAITING FOR DOCUMENTATION": t(
      "screens.trips.statusOptions.WAITING_FOR_DOCUMENTATION"
    ),
    COMPLETED: t("screens.trips.statusOptions.COMPLETED"),
    APPROVED: t("screens.trips.approvalStatus.APPROVED"),
    REJECTED: t("screens.trips.approvalStatus.REJECTED"),
    "TO APPROVE": t("screens.trips.approvalStatus.TO_APPROVE"),
    "AUTO ACCEPTED": t("screens.trips.approvalStatus.AUTO_ACCEPTED"),
    "TO BE EVALUATED": t("screens.trips.approvalStatus.TO_BE_EVALUATED"),
    "TO BE UPLOADED": t("screens.trips.approvalStatus.TO_BE_UPLOADED"),
    ACCEPTED: t("screens.trips.approvalStatus.ACCEPTED"),
  };

  return (
    <>
      <Box
        sx={{
          ...style.mainWrapper,
          width: isMobile ? "100%" : "100%",
          alignItems: isMobile ? "flex-start" : "flex-end",
          marginLeft: isMobile ? "8px" : "0",
        }}
      >
        {isMobile && <Button
          onClick={handleToggleDropdown}
          variant="text"
          startIcon={<IconFilterFilled />}
          sx={style.button1}
        >
          {t("screens.trips.filters.filters")}
        </Button>}

        {/*  DEsktopMode */}
        {/* Dropdown visibile solo quando isDropdownOpen è true */}
        {!isMobile && (

          <Box sx={dStyle.box1}>
            <Box sx={dStyle.box2}>
              {/* Filtro STATUS */}
              <Box sx={dStyle.filterBox}>
                <FormControl fullWidth>
                  <Typography sx={dStyle.filterLabel}>
                    {" "}
                    {t("screens.trips.filters.status")}
                  </Typography>
                  <Select
                    multiple
                    fullWidth={false}
                    value={filters?.milestoneValue || []}
                    renderValue={(selected) =>
                      formatSelectLabel(selected as string[])
                    }
                    onChange={(e) =>
                      handleFilterChange(
                        "milestoneValue",
                        e.target.value as string[]
                      )
                    }
                    sx={dStyle.filterSelect}
                  >
                    {Object.values(StatusOptions).map((status) => (
                      <MenuItem key={status} value={status} >
                        <Checkbox
                          checked={filters?.milestoneValue?.includes(status)}
                        />
                        <ListItemText
                          primary={translateMap[status].toUpperCase()}
                          primaryTypographyProps={{ fontSize: '12px' }}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Filtro APPROVAL */}
              <Box sx={dStyle.filterBox}>
                <FormControl fullWidth>
                  <Typography sx={dStyle.filterLabel}>
                    {" "}
                    {t("screens.trips.filters.approval")}
                  </Typography>
                  <Select
                    multiple
                    sx={dStyle.filterSelect}
                    value={filters?.approval || []}
                    renderValue={(selected) =>
                      formatSelectLabel(selected as string[])
                    }
                    onChange={(e) =>
                      handleFilterChange(
                        "approval",
                        e.target.value as string[]
                      )
                    }
                  >
                    {Object.values(ApprovalStatus).map((status) => (
                      <MenuItem key={status} value={status}>
                        <Checkbox
                          checked={filters?.approval?.includes(status)}
                        />
                        <ListItemText
                          primaryTypographyProps={{ fontSize: '12px' }}
                          primary={translateMap[status].toUpperCase()}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Filtro DATE */}
              <Box sx={dStyle.filterBox}>
                <FormControl fullWidth>
                  <Typography sx={dStyle.filterLabel}>
                    {" "}
                    {t("screens.trips.filters.date")}
                  </Typography>
                  <Select
                    sx={dStyle.filterSelect}
                    value={filters?.dateType ?? DateOptions.EXPECTED_DEPARTURE}
                    onChange={(e) =>
                      handleFilterChange("dateType", e.target.value as number)
                    }
                  >
                    <MenuItem sx={{ fontSize: '12px' }} value={DateOptions.ASSIGNED_DATE}>
                      {t("screens.trips.datesCard.assignedDate")}
                    </MenuItem>
                    <MenuItem sx={{ fontSize: '12px' }} value={DateOptions.EXPECTED_DEPARTURE}>
                      {t("screens.trips.datesCard.expectedDeparture")}
                    </MenuItem>
                    <MenuItem sx={{ fontSize: '12px' }} value={DateOptions.EXPECTED_ARRIVAL}>
                      {t("screens.trips.datesCard.expectedArrival")}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Filtro DATE RANGE */}
              <Box sx={dStyle.filterDrBox}>
                <Typography sx={dStyle.filterLabel}>
                  {" "}
                  {t("screens.trips.filters.dateRange")}
                </Typography>
                <ReactDatePicker

                  selected={startDate}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={handleDateChange}
                  customInput={
                    <CustomInput
                      startDate={startDate}
                      endDate={endDate}
                      isMobile={isMobile}
                      onClick={() => { }}
                    />
                  }
                  selectsRange
                  popperPlacement="bottom"
                  dateFormat="dd/MM/yyyy"
                />
              </Box>
              <Button
                variant="contained"
                color="primary"
                onClick={handleApplyFilters}
                sx={dStyle.filterSelect2}
                disabled={!areFiltersValid}
              >
                {t("screens.trips.filters.filter")}
              </Button>
            </Box>
          </Box>

        )}
      </Box>

      {/* MOBILE MODE */}
      {isMobile && (
        <Dialog
          open={isDropdownOpen}
          onClose={() => setIsDropdownOpen(false)}
          fullWidth
          slotProps={{
            backdrop: {
              style: mStyle.dialogSlotProps,
            },
          }}
        >
          <Box sx={mStyle.box1}>
            <Box sx={mStyle.box2}>
              <DialogTitle sx={mStyle.dialogTitle}>
                {" "}
                {t("screens.trips.filters.filters")}
              </DialogTitle>
              <IconButton onClick={() => setIsDropdownOpen(false)}>
                <Close sx={mStyle.iconButton} />
              </IconButton>
            </Box>
            <DialogContent sx={mStyle.dialogContent}>
              <Box sx={mStyle.box3}>
                <Box sx={mStyle.box4}>
                  {/* Filtro STATUS */}
                  <FormControl fullWidth>
                    <Typography sx={mStyle.filterLabel}>
                      {" "}
                      {t("screens.trips.filters.status")}
                    </Typography>
                    <Select
                      fullWidth
                      multiple
                      value={filters?.milestoneValue || []}
                      renderValue={(selected) =>
                        formatSelectLabel(selected as string[])
                      }
                      onChange={(e) =>
                        handleFilterChange(
                          "milestoneValue",
                          e.target.value as string[]
                        )
                      }
                      sx={mStyle.filterSelect}
                    >
                      {Object.values(StatusOptions).map((status) => (
                        <MenuItem key={status} value={status}>
                          <Checkbox
                            checked={filters?.milestoneValue?.includes(status)}
                          />
                          <ListItemText
                            primary={translateMap[status].toUpperCase()}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Filtro APPROVAL */}
                  <FormControl fullWidth>
                    <Typography sx={mStyle.filterLabel}>
                      {" "}
                      {t("screens.trips.filters.approval")}
                    </Typography>
                    <Select
                      multiple
                      sx={mStyle.filterSelect}
                      value={filters?.approval || []}
                      renderValue={(selected) =>
                        formatSelectLabel(selected as string[])
                      }
                      onChange={(e) =>
                        handleFilterChange(
                          "approval",
                          e.target.value as string[]
                        )
                      }
                    >
                      {Object.values(ApprovalStatus).map((status) => (
                        <MenuItem key={status} value={status}>
                          <Checkbox
                            checked={filters?.approval?.includes(status)}
                          />
                          <ListItemText
                            primary={translateMap[status].toUpperCase()}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Filtro DATE */}
                  <FormControl fullWidth>
                    <Typography sx={mStyle.filterLabel}>
                      {" "}
                      {t("screens.trips.filters.date")}
                    </Typography>
                    <Select
                      sx={mStyle.filterSelect}
                      value={filters?.dateType ?? DateOptions.EXPECTED_DEPARTURE}
                      onChange={(e) =>
                        handleFilterChange("dateType", e.target.value as number)
                      }
                    >
                      <MenuItem value={DateOptions.ASSIGNED_DATE}>
                        {t("screens.trips.datesCard.assignedDate")}
                      </MenuItem>
                      <MenuItem value={DateOptions.EXPECTED_DEPARTURE}>
                        {t("screens.trips.datesCard.expectedDeparture")}
                      </MenuItem>
                      <MenuItem value={DateOptions.EXPECTED_ARRIVAL}>
                        {t("screens.trips.datesCard.expectedArrival")}
                      </MenuItem>
                    </Select>
                  </FormControl>

                  {/* Filtro DATE RANGE */}
                  <Box>
                    <Typography sx={mStyle.filterLabel}>
                      {" "}
                      {t("screens.trips.filters.dateRange")}
                    </Typography>
                    <ReactDatePicker
                      selected={startDate}
                      startDate={startDate}
                      endDate={endDate}
                      onChange={handleDateChange}
                      customInput={
                        <CustomInput
                          startDate={startDate}
                          endDate={endDate}
                          isMobile={isMobile}
                          onClick={() => { }}
                        />
                      }
                      selectsRange
                      popperPlacement="bottom"
                      maxDate={new Date()}
                      minDate={(() => {
                        const minDate = new Date();
                        minDate.setDate(minDate.getDate() - 30);
                        return minDate;
                      })()}
                      dateFormat="dd/MM/yyyy"
                    />
                  </Box>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={mStyle.dialogAction}>
              <Button
                fullWidth={false}
                variant="contained"
                color="primary"
                onClick={handleApplyFilters}
                sx={mStyle.filterSelect}
                disabled={!areFiltersValid}
              >
                {t("screens.trips.filters.filter")}
              </Button>
            </DialogActions>
          </Box>
        </Dialog>
      )}
    </>
  );
};

export default FilterBar;
